// react
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

// components
import { Button, Keni } from '@/components/atoms';
import { OnboardingEditor } from '@/components/organisms';
import { OnboardingActionType, OnboardingFlow } from '@/components/pages';

// validation
const validateNotEmpty = (value?: string) =>
  (value && value.length > 0) ||
  (typeof value === 'boolean' && value !== undefined);

interface EligibilityCalculatorProps {
  className?: string;
  fields?: any;
  answers: { [key: string]: string };
  setAnswers: (answers: any) => void;
  onUpdateCareProfile: () => Promise<boolean>;
  onEligibilityStatusChange?: (status: string) => void;
}

/**
 * EligibilityCalculator calculates whether a user is eligible for attendance
 * allowance.
 */
export const EligibilityCalculator = ({
  className,
  fields,
  answers,
  setAnswers,
  onUpdateCareProfile,
  onEligibilityStatusChange,
}: EligibilityCalculatorProps) => {
  // state
  const [isRetake, setIsRetake] = useState(false);

  // refs
  const wrapperRef = useRef<HTMLDivElement>(null);

  // methods
  const scrollIntoView = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return true;

    el.scrollIntoView({ behavior: 'smooth' });

    return true;
  }, [wrapperRef]);

  const handleComplete = useCallback(async () => {
    await onUpdateCareProfile();
    setIsRetake(false);
  }, [onUpdateCareProfile]);

  // memos
  const eligibilityConfig = useMemo(() => {
    return [
      {
        id: 'care-profile-questions',
        Component: OnboardingEditor,
        onBeforeNext: async () => scrollIntoView(),
        config: [
          ...Object.keys(fields || {}).map((id: any, i: number) => {
            const f = fields?.[id];

            return {
              id: f.id,
              title: f.label,
              subTitle: f.supportText,
              tooltip: f.tooltip,
              value: { value: answers?.[f.id] },
              onChange: (value: any) => {
                setAnswers({
                  ...answers,
                  [f.id]: value.value,
                });
              },
              size: 'sm',
              fieldDef: {
                ...f,
                isHideTitle: true,
                validation: validateNotEmpty,
                isRequired: false,
              },
              actions: [
                ...(i !== 0
                  ? [
                      {
                        label: 'Back',
                        onClick: OnboardingActionType.Back,
                      },
                    ]
                  : []),
                {
                  label: 'Next',
                  onClick: OnboardingActionType.Next,
                },
              ],
            };
          }),
        ],
      },
    ];
  }, [answers, fields, setAnswers, onUpdateCareProfile, wrapperRef]);

  const eligibilityStatus = useMemo(() => {
    if (isRetake) return 'incomplete';

    const ans = {
      location: answers['care-recipient-location'],
      isOver65: answers['care-recipient-is-over-65'],
      healthCondition: answers['care-recipient-health-condition'],
      previousSupervision: answers['care-recipient-previous-supervision'],
      terminallyIll: answers['care-recipient-terminally-ill'],
      helpNeeded: answers['care-recipient-help-needed'],
    };

    for (let i = 0; i < Object.keys(ans).length; i++) {
      const key = Object.keys(ans)[i];
      const val = ans[key as keyof typeof ans];
      if (!val) {
        return 'incomplete';
      }
    }

    // location
    const isInEngland = ans.location === 'care-recipient-location-england';

    // state pension age
    const isStatePensionAge = ans.isOver65 === 'true';

    // health condition
    const isHealthCondition = ans.healthCondition === 'true';

    // previous supervision
    const isPreviousSupervision = ans.previousSupervision === 'true';

    // terminally ill
    const isTerminallyIll = ans.terminallyIll === 'true';

    // help needed
    const isHelpNeeded = JSON.parse(ans.helpNeeded || '[]').length > 0;

    // eligibility
    const eligibilityCount = [
      isHealthCondition,
      isPreviousSupervision,
      isTerminallyIll,
      isHelpNeeded,
    ].filter((x) => x).length;

    // eligibility conditions
    if (!isInEngland) return 'not-eligible';
    if (!isStatePensionAge) return 'not-eligible';

    if (eligibilityCount == 1) return 'eligible';
    if (eligibilityCount > 1) return 'very-eligible';

    return 'not-eligible';
  }, [answers, isRetake]);

  const eligibilityRender = useMemo(() => {
    switch (eligibilityStatus) {
      case 'very-eligible':
      case 'eligible':
      case 'not-eligible':
        const isEligible = eligibilityStatus !== 'not-eligible';
        return (
          <Result>
            <KeniWrapper>
              <KeniBorder>
                <Keni
                  emotion={isEligible ? 'happy' : 'sad'}
                  keniWidth='100px'
                />
              </KeniBorder>
            </KeniWrapper>
            <div>
              {eligibilityStatus === 'very-eligible' &&
                'From our calculations, it looks like you are very likely to be eligible to claim for Attendance Allowance'}
              {eligibilityStatus === 'eligible' &&
                'From our calculations, it looks like you may be eligible to claim for Attendance Allowance'}
              {eligibilityStatus === 'not-eligible' &&
                'From our calculations it doesn’t look like you’re eligible to claim Attendance Allowance'}
            </div>
            <Button
              ariaLabel='retake the test'
              variant='secondary'
              size='sm'
              iconLeft='rotate-right'
              onPress={() => setIsRetake(true)}
            >
              Retake the test
            </Button>
          </Result>
        );
    }
    return null;
  }, [eligibilityStatus]);

  useEffect(() => {
    onEligibilityStatusChange && onEligibilityStatusChange(eligibilityStatus);
  }, [eligibilityStatus, onEligibilityStatusChange]);

  return (
    <Wrapper ref={wrapperRef}>
      {eligibilityRender}
      {eligibilityStatus === 'incomplete' && (
        <OnboardingFlow
          className={className}
          config={eligibilityConfig}
          onComplete={handleComplete}
          onNext={scrollIntoView}
          isActionBackground={false}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  scroll-margin-top: ${(props) => props.theme.spacing[32]};
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  justify-content: center;
  align-items: center;
  text-align: center;
  font: ${(props) => props.theme.typography.body.lg.regular};
  padding: ${(props) => props.theme.spacing[32]};
`;

const KeniWrapper = styled.div`
  border-radius: 999999px;
  width: 120px;
  max-width: 120px;
  height: 120px;
  max-height: 120px;
  overflow: hidden;
  padding: ${(props) => props.theme.spacing[8]};
  background: ${(props) => props.theme.color.primary[80]};
`;

const KeniBorder = styled.div`
  border-radius: 999999px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.color.custom.fadeUp};
`;

EligibilityCalculator.displayName = 'EligibilityCalculator';

export default EligibilityCalculator;
